import cn from "classnames";
import { useState } from "react";
import { TranscriptSectionIndexDB } from "../../classes/audioDb";
import RefreshIcon from "@icons/refresh-cw-03.svg?react";
import DownloadIcon from "@icons/download-01.svg?react";
import TrashIcon from "@icons/trash-03.svg?react";
import { ThreeDotMenu, MenuItemConfig } from "../ThreeDotMenu";
import Spinner from "../Spinner";
import { useAppSelector } from "@/common/hooks/useRedux";
import { selectTableRecordById } from "@/state/redux/patientTableRecordsSlice";
import { PatientRecord } from "@/common/utils/types";

interface LocalRecordingActionsProps {
    recording: TranscriptSectionIndexDB;
    separateRetryButton?: boolean;
    showDeleteNow?: boolean;
    handleUpload: (recording: TranscriptSectionIndexDB, existingPatientRecord: PatientRecord | null) => Promise<void>;
    handleDownload: (recording: TranscriptSectionIndexDB) => void;
    handleDelete: (recording: TranscriptSectionIndexDB) => Promise<void>;
}

const UNKNOWN_VISIT_LABEL = "Can't upload: visit unknown — download instead";

const LocalRecordingActions: React.FC<LocalRecordingActionsProps> = ({
    recording,
    separateRetryButton = false,
    showDeleteNow = false,
    handleUpload: onUpload,
    handleDownload: onDownload,
    handleDelete: onDelete,
}) => {
    const [isUploadingLocal, setIsUploadingLocal] = useState(false);
    const isIncompleteUpload = recording.bytesUploaded < recording.audioData.size;
    // EN-1609: an extension recording handed back without a linkable visit has no transcript id. A
    // retry can't succeed and its bulk fallback would write an orphaned storage object, so only
    // Download is offered.
    const visitUnknown = !recording.transcriptId;
    const existingPatientRecord = useAppSelector((state) =>
        selectTableRecordById(state, recording?.patientRecordId || ""),
    );

    const handleUploadClick = async () => {
        setIsUploadingLocal(true);
        try {
            await onUpload(recording, existingPatientRecord);
        } finally {
            setIsUploadingLocal(false);
        }
    };

    const handleDownloadClick = () => {
        onDownload(recording);
    };

    const handleDeleteClick = async () => {
        await onDelete(recording);
    };

    // Build menu items for ThreeDotMenu
    const menuItems: MenuItemConfig[] = [];

    // Add Download action
    menuItems.push({
        id: "download",
        content: "Download",
        icon: DownloadIcon,
        onClick: handleDownloadClick,
    });

    // Add Retry action only if incomplete upload
    if (!separateRetryButton && isIncompleteUpload) {
        menuItems.push({
            id: "retry",
            content: visitUnknown ? UNKNOWN_VISIT_LABEL : "Retry Upload",
            icon: RefreshIcon,
            onClick: handleUploadClick,
            disabled: visitUnknown,
        });
    }

    // Add Delete action if showDeleteNow is true
    if (showDeleteNow) {
        menuItems.push({
            id: "delete",
            content: "Delete Now",
            icon: TrashIcon,
            className: "text-error-600",
            onClick: handleDeleteClick,
        });
    }

    return (
        <div className="flex items-center space-x-2">
            {separateRetryButton && isIncompleteUpload && (
                <>
                    {isUploadingLocal ? (
                        <button
                            className={cn(
                                "w-full rounded-md border border-gray-300 flex items-center justify-center text-center gap-2 bg-white px-3.5 py-1.5 text-sm font-semibold text-gray-600 shadow-sm focused",
                            )}
                        >
                            <Spinner size="xsmall" color="text-gray-900" />
                            <span>Loading</span>
                        </button>
                    ) : visitUnknown ? (
                        <button
                            disabled
                            title={UNKNOWN_VISIT_LABEL}
                            aria-label={UNKNOWN_VISIT_LABEL}
                            className="w-full rounded-md flex items-center justify-center text-center gap-2 bg-max-900 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm opacity-50 cursor-not-allowed"
                        >
                            <RefreshIcon className="h-4 w-4 text-white font-semibold" />
                            <span>Retry</span>
                        </button>
                    ) : (
                        <button
                            className={cn(
                                "w-full rounded-md flex items-center justify-center text-center gap-2 bg-max-900 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-max-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-max-900",
                            )}
                            onClick={handleUploadClick}
                        >
                            <RefreshIcon className="h-4 w-4 text-white font-semibold" />
                            <span>Retry</span>
                        </button>
                    )}
                </>
            )}
            <ThreeDotMenu
                buttonClassName="border border-gray-300 rounded-md w-8 h-8 flex items-center justify-center bg-white text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focused"
                menuItems={menuItems}
            />
        </div>
    );
};

export default LocalRecordingActions;
